/**
 * Tool glyphs — one minimal, geometric, single-focal-point line icon
 * per tool, shared across every suite. 24×24 viewBox, 1.8 stroke,
 * stroke="currentColor" so each glyph inherits its suite's locked
 * accent hue from ToolCard (no per-suite color logic here — cohesion
 * is structural). Style follows 2026 "geometric neo-minimalism" which
 * the evidence backs for study/productivity tools (scannable at the
 * 38px card size, calm, consistent with the parent letter-marks).
 *
 * Keyed by exact tool `name` strings used in the suite home files.
 */
import { cloneElement, Children, isValidElement } from "react";

const S = {
  width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
  stroke: "currentColor", strokeWidth: 1.8,
  strokeLinecap: "round", strokeLinejoin: "round",
};
// g() stores the glyph's path children only. glyphFor() returns the
// default wrapped <svg> (currentColor, 22px) so existing callers are
// unchanged. renderGlyph() builds a sized, gradient-or-solid-stroked
// SVG from the SAME stored children — no glyph body is ever duplicated.
const g = (children) => ({ __glyph: true, children });

export const TOOL_GLYPHS = {
  // ---- NerdChem ----
  "Periodic Table": g(<>
    <rect x="3" y="3" width="6" height="6" rx="1" /><rect x="3" y="15" width="6" height="6" rx="1" />
    <rect x="15" y="15" width="6" height="6" rx="1" /><path d="M11 6h10M6 11v8" />
  </>),
  "Molecular Weight": g(<>
    <circle cx="7" cy="9" r="2.5" /><circle cx="17" cy="7" r="2.5" /><circle cx="14" cy="17" r="2.5" />
    <path d="M9 10l5 5M9.5 8l5-1" />
  </>),
  "Mole Calculator": g(<>
    <rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h3M8 16h3M15 12v5" />
  </>),
  "pH / pOH Calculator": g(<>
    <path d="M9 3v6l-4 8a3 3 0 0 0 3 4h8a3 3 0 0 0 3-4l-4-8V3" /><path d="M8 3h8M7 15h10" />
  </>),
  "Dilution Calculator": g(<>
    <path d="M6 3h12l-1 7a5 5 0 0 1-10 0z" /><path d="M7.5 11h9" /><path d="M12 16v5M9 21h6" />
  </>),
  "Significant Figures": g(<>
    <text x="12" y="18.5" textAnchor="middle" fontSize="20" fontWeight="700" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">23</text>
  </>),
  "Equation Balancer": g(<>
    <path d="M12 3v18M5 8h14" /><circle cx="7" cy="13" r="2.5" /><circle cx="17" cy="13" r="2.5" />
  </>),
  "Gas Law Solver": g(<>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="8" cy="9" r="0.7"/><circle cx="13" cy="8" r="0.7"/><circle cx="17" cy="11" r="0.7"/><circle cx="10" cy="13" r="0.7"/><circle cx="15" cy="15" r="0.7"/><circle cx="7" cy="16" r="0.7"/>
  </>),
  "Stoichiometry": g(<>
    <path d="M4 7h7M4 7l2-2M4 7l2 2M20 17h-7M20 17l-2-2M20 17l-2 2" /><path d="M11 7l2 10" />
  </>),

  // ---- NerdMath ----
  "Scientific Calculator": g(<>
    <rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8" />
    <circle cx="8" cy="12" r="0.9"/><circle cx="12" cy="12" r="0.9"/><circle cx="16" cy="12" r="0.9"/>
    <circle cx="8" cy="16" r="0.9"/><circle cx="12" cy="16" r="0.9"/><circle cx="16" cy="16" r="0.9"/>
  </>),
  "Quadratic Solver": g(<>
    <path d="M4 4 Q12 26 20 4" /><path d="M3 19h18" />
  </>),
  "Trig & Unit Circle": g(<>
    <circle cx="12" cy="12" r="9" /><path d="M12 12h9M12 12L10.4 3.1" /><path d="M17.5 12A5.5 5.5 0 0 0 11 6.6" />
  </>),
  "Logarithm": g(<>
    <path d="M4 18c3 0 5-12 16-12" /><path d="M4 4v16h16" />
  </>),
  "Matrix": g(<>
    <circle cx="6" cy="6" r="1.1"/><circle cx="12" cy="6" r="1.1"/><circle cx="18" cy="6" r="1.1"/>
    <circle cx="6" cy="12" r="1.1"/><circle cx="12" cy="12" r="1.1"/><circle cx="18" cy="12" r="1.1"/>
    <circle cx="6" cy="18" r="1.1"/><circle cx="12" cy="18" r="1.1"/><circle cx="18" cy="18" r="1.1"/>
  </>),

  // ---- NerdPhys ----
  "Projectile Motion": g(<>
    <path d="M4 20C6 10 14 4 20 4" strokeDasharray="0.1 3" /><circle cx="20" cy="4" r="2" /><path d="M3 21h6" />
  </>),
  "Kinematics (SUVAT)": g(<>
    <g transform="translate(12 12) scale(1.18) translate(-12 -12)"><path d="M3 12h14M17 12l-4-4M17 12l-4 4" /><path d="M3 6v12" /></g>
  </>),
  "Newton's 2nd Law": g(<>
    <g transform="translate(12 12) scale(1.15) translate(-12 -12)"><rect x="4" y="8" width="9" height="9" rx="1" /><path d="M13 12.5h6M19 12.5l-3-3M19 12.5l-3 3" /></g>
  </>),
  "Energy & Work": g(<>
    <path d="M13 2L5 14h6l-1 8 8-12h-6z" />
  </>),
  "Ohm's Law": g(<>
    <g transform="translate(12 12) scale(1.15) translate(-12 -12)"><path d="M3 12h4l2-5 3 10 3-10 2 5h4" /></g>
  </>),

  // ---- NerdTools ----
  "Unit Converter": g(<>
    <path d="M4 8h13M14 5l3 3-3 3M20 16H7M10 13l-3 3 3 3" />
  </>),
  "Bubble Level": g(<>
    <rect x="3" y="9" width="18" height="6" rx="2" /><circle cx="12" cy="12" r="1.6" /><path d="M9 9v6M15 9v6" />
  </>),
  "Compass": g(<>
    <circle cx="12" cy="12" r="10" /><path d="M12 5l3.2 7H8.8z" /><path d="M12 19l-3.2-7h6.4z" /><circle cx="12" cy="12" r="0.7" />
  </>),
  "Decibel Meter": g(<>
    <path d="M4 14v-4h3l4-4v12l-4-4H4z" /><path d="M15 9a4 4 0 0 1 0 6M17.5 7a7 7 0 0 1 0 10" />
  </>),
  "Screen Ruler": g(<>
    <rect x="2" y="7" width="20" height="10" rx="1" /><path d="M6 7v4M10 7v3M14 7v4M18 7v3" />
  </>),
  "QR Code": g(<>
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" /><path d="M14 14h3v3M21 14v7M17 21h4M14 19v2" />
  </>),

  // ---- NerdBio ----
  "DNA / RNA Toolkit": g(<>
    <path d="M8 3c0 6 8 6 8 12s-8 6-8 12M16 3c0 6-8 6-8 12s8 6 8 12" /><path d="M9 7h6M9 12h6M9 17h6" />
  </>),
  "Codon Translator": g(<>
    <rect x="3" y="5" width="18" height="5" rx="1" /><rect x="3" y="14" width="18" height="5" rx="1" />
    <path d="M7 10v4M12 10v4M17 10v4" />
  </>),
  "Punnett Square": g(<>
    <rect x="4" y="4" width="16" height="16" rx="1" /><path d="M12 4v16M4 12h16" />
  </>),
  "Hardy-Weinberg": g(<>
    <path d="M3 20c4 0 4-14 9-14s5 14 9 14" /><path d="M3 20h18" />
  </>),
  "Lab Solutions": g(<>
    <path d="M10 3v7l-5 8a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-8V3" /><path d="M9 3h6M7.5 16h9" />
  </>),
  "Anatomy Reference": g(<>
    <circle cx="12" cy="5" r="2.5" /><path d="M12 7.5v8M12 10l-5 3M12 10l5 3M12 15.5l-3 5M12 15.5l3 5" />
  </>),

  // ---- Shared "book" reference glyph (V49+) ----
  // Open book with a center spine + two angled pages. Used for every
  // family-level reference tile EXCEPT Anatomy (which has its own person
  // glyph and stays distinct). Future general Bio Reference and Math
  // Reference tiles use the same shape.
  "Physics Reference": g(<>
    <path d="M3 5l9-1v17l-9 1zM21 5l-9-1v17l9 1z" /><path d="M12 4v17" /><path d="M5.5 8.5l5-.6M5.5 12l5-.6M5.5 15.5l5-.6M18.5 8.5l-5-.6M18.5 12l-5-.6M18.5 15.5l-5-.6" />
  </>),
  "Chemistry Reference": g(<>
    <path d="M3 5l9-1v17l-9 1zM21 5l-9-1v17l9 1z" /><path d="M12 4v17" /><path d="M5.5 8.5l5-.6M5.5 12l5-.6M5.5 15.5l5-.6M18.5 8.5l-5-.6M18.5 12l-5-.6M18.5 15.5l-5-.6" />
  </>),
  "Biology Reference": g(<>
    <path d="M3 5l9-1v17l-9 1zM21 5l-9-1v17l9 1z" /><path d="M12 4v17" /><path d="M5.5 8.5l5-.6M5.5 12l5-.6M5.5 15.5l5-.6M18.5 8.5l-5-.6M18.5 12l-5-.6M18.5 15.5l-5-.6" />
  </>),
  "Math Reference": g(<>
    <path d="M3 5l9-1v17l-9 1zM21 5l-9-1v17l9 1z" /><path d="M12 4v17" /><path d="M5.5 8.5l5-.6M5.5 12l5-.6M5.5 15.5l5-.6M18.5 8.5l-5-.6M18.5 12l-5-.6M18.5 15.5l-5-.6" />
  </>),
  "Circle of Fifths": g(<>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4" />
  </>),
  "Scale Builder": g(<>
    <rect x="3" y="6" width="18" height="13" rx="1" />
    <path d="M8 6v13M13 6v13M18 6v13" />
    <rect x="6.25" y="6" width="2" height="7" fill="currentColor" />
    <rect x="11.25" y="6" width="2" height="7" fill="currentColor" />
    <rect x="16.25" y="6" width="2" height="7" fill="currentColor" />
  </>),
  "Music Reference": g(<>
    <path d="M3 5l9-1v17l-9 1zM21 5l-9-1v17l9 1z" /><path d="M12 4v17" /><path d="M5.5 8.5l5-.6M5.5 12l5-.6M5.5 15.5l5-.6M18.5 8.5l-5-.6M18.5 12l-5-.6M18.5 15.5l-5-.6" />
  </>),
  "History Reference": g(<>
    <path d="M3 5l9-1v17l-9 1zM21 5l-9-1v17l9 1z" /><path d="M12 4v17" /><path d="M5.5 8.5l5-.6M5.5 12l5-.6M5.5 15.5l5-.6M18.5 8.5l-5-.6M18.5 12l-5-.6M18.5 15.5l-5-.6" />
  </>),

  // ---- NerdStack root tools ----
  "GradeCalc": g(<>
    <rect x="4" y="2" width="16" height="20" rx="2" /><path d="M8 8l2 2 4-4M8 15h8" />
  </>),
  "StudyTimer": g(<>
    <circle cx="12" cy="13" r="8" /><path d="M12 13V8M12 13l4 2M9 2h6" />
  </>),
  "WordCount": g(<>
    <path d="M3 5h18M3 12h18M3 19h11" />
  </>),
  "PercentCalc": g(<>
    <circle cx="7" cy="7" r="2.8" /><circle cx="17" cy="17" r="2.8" /><path d="M5 19L19 5" />
  </>),
  "UnitConvert": g(<>
    <path d="M3 8h14M14 5l3 3-3 3M21 16H7M10 13l-3 3 3 3" />
  </>),
  "AverageCalc": g(<>
    <path d="M4 19V11M10 19V6M16 19v-9M21 19v-5" /><path d="M3 19h18" /><path d="M3 14h18" strokeDasharray="0.1 2.6" />
  </>),
  "QuizCard": g(<>
    <rect x="2" y="6" width="14" height="16" rx="2" /><rect x="8" y="2" width="14" height="16" rx="2" /><path d="M11 7h8M11 11h5" />
  </>),
  "ClearLens": g(<>
    <circle cx="10" cy="10" r="7" /><path d="M15 15l6 6M7 10h6M10 7v6" />
  </>),
  "Suggest a tool": g(<>
    <path d="M12 4v16M4 12h16" />
  </>),
  "Particle Reference": g(<>
    <circle cx="12" cy="12" r="2" /><ellipse cx="12" cy="12" rx="10" ry="4" /><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" /><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" />
  </>),
  "Wave Calculator": g(<>
    <path d="M2 12c2.5 0 2.5-7 5-7s2.5 14 5 14 2.5-7 5-7" />
  </>),
  "Momentum & Collisions": g(<>
    <circle cx="6" cy="14" r="4" /><circle cx="18" cy="14" r="4" /><path d="M10 8l3-3M13 8l-3-3" />
  </>),

  // ---- NerdGames (the 5 learning games — same line-glyph family) ----
  // Periodic Quest — a single periodic-table element cell (generic,
  // pure paths: cell + symbol/number baselines). Distinct from the
  // full-table "Periodic Table" glyph.
  "Periodic Quest": g(<>
    <rect x="4" y="4" width="16" height="16" rx="2" /><path d="M7 8h3M7 16h10" />
  </>),
  // Gene Pool — the DNA double-helix (single focal idea; the "pool"
  // pun is the name's job, the glyph carries the biology — same logic
  // as Codon Translator. Echoes the DNA / RNA Toolkit family).
  "Gene Pool": g(<>
    <path d="M8 3c0 6 8 6 8 12s-8 6-8 12M16 3c0 6-8 6-8 12s8 6 8 12" /><path d="M9 7h6M9 12h6M9 17h6" />
  </>),
  // Anatomy Park — the Anatomy figure inside a circular badge: clearly
  // a person (echoes "Anatomy Reference") but enclosed so it reads as
  // the *game*, distinct from the bare reference glyph on shared surfaces.
  "Anatomy Park": g(<>
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="7" r="1.7" />
    <path d="M12 8.7v5M12 10l-3 2M12 10l3 2M12 13.7l-2 4M12 13.7l2 4" />
  </>),

  // V45.1 — 5 new NerdBio games. Set designed coherently so they read
  // together on /games/bio. All gradient-stroked in the locked V33 style.

  // Pedigree — three-generation family tree. Square = male, circle =
  // female, lines = parentage. The canonical visual for inheritance
  // pattern recognition.
  "Pedigree": g(<>
    <rect x="5" y="3" width="4" height="4" />
    <circle cx="17" cy="5" r="2" />
    <path d="M9 5h6" />
    <path d="M12 7v3" />
    <rect x="6" y="11" width="3.5" height="3.5" />
    <circle cx="16.5" cy="12.75" r="1.75" />
    <path d="M12 14.5v3" />
    <circle cx="12" cy="20" r="1.8" />
  </>),

  // Phase Lab — three-stage cell-division progression. Sequential
  // shrinking-then-growing cells say "this cell is going through phases"
  // without picking a specific phase (mitosis or meiosis as choice).
  "Phase Lab": g(<>
    <circle cx="6" cy="12" r="3.5" />
    <circle cx="13.5" cy="12" r="3.5" />
    <circle cx="20" cy="12" r="2.5" />
    <path d="M9.5 12h.5M17 12h.5" />
  </>),

  // Selection Pressure — four-organism population at top, arrow pointing
  // down, two survivors centered under (the survivors filled to show
  // they're the "selected" ones). Survivorship is the literal mental
  // model of selection.
  "Selection Pressure": g(<>
    <circle cx="6" cy="5" r="1.4" />
    <circle cx="11" cy="5" r="1.4" />
    <circle cx="16" cy="5" r="1.4" />
    <circle cx="21" cy="5" r="1.4" />
    <path d="M13.5 8v6M11 12l2.5 2 2.5-2" />
    <circle cx="9" cy="19" r="1.4" fill="GLYPH_PAINT" stroke="none" />
    <circle cx="18" cy="19" r="1.4" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Codon Reader — 2×2 grid with A T G C letters. The four bases of
  // the genetic alphabet. Breaks the "no letters in tool glyphs"
  // convention deliberately (Ian's call); the literal labels read
  // instantly as "the four nucleotides" and the game IS about reading
  // sequences of these four. Letters use GLYPH_PAINT so they render
  // in the same gradient as the cell strokes.
  "Codon Reader": g(<>
    <rect x="3" y="3" width="8.5" height="8.5" rx="1" />
    <rect x="12.5" y="3" width="8.5" height="8.5" rx="1" />
    <rect x="3" y="12.5" width="8.5" height="8.5" rx="1" />
    <rect x="12.5" y="12.5" width="8.5" height="8.5" rx="1" />
    <text x="7.25" y="9.5" textAnchor="middle" fill="GLYPH_PAINT" stroke="none"
          fontSize="6.5" fontWeight="600"
          fontFamily="DM Sans, system-ui, sans-serif">A</text>
    <text x="16.75" y="9.5" textAnchor="middle" fill="GLYPH_PAINT" stroke="none"
          fontSize="6.5" fontWeight="600"
          fontFamily="DM Sans, system-ui, sans-serif">T</text>
    <text x="7.25" y="19" textAnchor="middle" fill="GLYPH_PAINT" stroke="none"
          fontSize="6.5" fontWeight="600"
          fontFamily="DM Sans, system-ui, sans-serif">G</text>
    <text x="16.75" y="19" textAnchor="middle" fill="GLYPH_PAINT" stroke="none"
          fontSize="6.5" fontWeight="600"
          fontFamily="DM Sans, system-ui, sans-serif">C</text>
  </>),

  // Membrane — lipid bilayer (two parallel lines) with embedded channel
  // protein (rounded rect spanning both layers) and ions on inside +
  // outside. The protein-in-membrane visual matches the actual game
  // mechanic (transport via channels and pumps).
  "Membrane": g(<>
    <path d="M3 10h7M14 10h7" />
    <path d="M3 14h7M14 14h7" />
    <rect x="10" y="7" width="4" height="10" rx="0.5" />
    <circle cx="6" cy="6" r="1" fill="GLYPH_PAINT" stroke="none" />
    <circle cx="18" cy="18" r="1" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Ménage à Trig — unit circle with THREE radii at 120° (the "three";
  // a glyph, not a bare digit — won't collide with the parent letters).
  "Ménage à Trig": g(<>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 12V3M12 12l7.8 4.5M12 12l-7.8 4.5" />
  </>),
  // Mathic Quest — "≈" approximately-equal, two tight tilde strokes
  // (number-sense / estimation; symbol-like, NOT rolling waves).
  "Mathic Quest": g(<>
    <path d="M5 10q2.5-2.5 5 0t5 0" /><path d="M5 15q2.5-2.5 5 0t5 0" />
  </>),
  // QuarkForge — three quarks bound inside a confinement circle. The
  // pyramid of dots IS a baryon as physicists draw it on whiteboards;
  // the containing circle anchors the design to the family aesthetic
  // (Anatomy Park, Ménage à Trig both use the badge-circle motif) and
  // is scientifically accurate to quark confinement.
  // Note: dot fill uses GLYPH_PAINT sentinel (NOT currentColor) so the
  // dots render in the same gradient as the outer stroke — currentColor
  // would fall back to CSS-inherited color and read as a different,
  // dimmer hue.
  "QuarkForge": g(<>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="8" r="2" fill="GLYPH_PAINT" stroke="none" />
    <circle cx="8" cy="15" r="2" fill="GLYPH_PAINT" stroke="none" />
    <circle cx="16" cy="15" r="2" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // V63.3 — Mission Control. Literal ΔV text — the universal
  // patched-conics shorthand for "change in velocity". Uses <text>
  // following the "Significant Figures" precedent in this file
  // (literal characters in the glyph viewbox rather than path-based
  // marks). V63.3.1 fix — earlier path-based version read as a
  // stylized ship at 38px instead of as the Δ+V mark.
  "Mission Control": g(<>
    <text x="12" y="18.5" textAnchor="middle" fontSize="18" fontWeight="700" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">ΔV</text>
  </>),

  // V63.1 — 1 NerdHistory game (first in the family).

  // Roman Province — Doric column: base, fluted shaft, capital.
  // The column is the most universally-readable Roman icon, neutral
  // between Republic and Empire, and works at the 38px card size
  // without losing legibility. Two horizontal "fluting" strokes on
  // the shaft add Roman-ness without crowding.
  "Roman Province": g(<>
    <rect x="5" y="19" width="14" height="2" rx="0.5" />
    <rect x="5" y="3" width="14" height="2" rx="0.5" />
    <rect x="7" y="5" width="10" height="14" />
    <line x1="10" y1="8" x2="10" y2="16" />
    <line x1="14" y1="8" x2="14" y2="16" />
  </>),

  // V63.2 — first NerdMusic game.
  // V63.2.1 — Film cell glyph. Outer strip + 2 horizontal dividers
  // splitting top-perforation / image / bottom-perforation; 3 short
  // vertical tick marks per strip read as sprocket holes. Universally
  // recognizable as "film" at the 38px card size. Previously was a
  // clapperboard-and-waveform that didn't read as cleanly.
  "Film Scoring": g(<>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 8h18M3 16h18" />
    <path d="M7 4v3M12 4v3M17 4v3" />
    <path d="M7 17v3M12 17v3M17 17v3" />
  </>),


  // V45 — 2 NerdChem games.

  // ChemForge — Bohr-model atom: a central nucleus dot with two
  // electron orbits crossing through it. The atom symbol is one of the
  // most universally-recognized scientific icons.
  "ChemForge": g(<>
    <circle cx="12" cy="12" r="2.5" fill="GLYPH_PAINT" stroke="none" />
    <ellipse cx="12" cy="12" rx="9" ry="3.5" />
    <ellipse cx="12" cy="12" rx="9" ry="3.5" transform="rotate(60 12 12)" />
  </>),

  // MoleForge — two atoms joined by a bond. The simplest possible
  // molecule glyph: a circle, a line, a circle. Says "atoms bond to
  // make molecules" at a glance.
  "MoleForge": g(<>
    <circle cx="7" cy="12" r="3.5" />
    <circle cx="17" cy="12" r="3.5" />
    <path d="M10.5 12h3" />
  </>),

  // V44 — 10 NerdMath games. Geometric neo-minimalism, single focal
  // idea each, 24×24 viewBox, ~1.8 stroke. Mathematical glyphs lean
  // hard into "the math is the design."

  // Number Line Drop — a number line with a single pin/drop marker.
  // The line + tick + droplet says "place a value here."
  "Number Line Drop": g(<>
    <path d="M3 16h18M7 14v4M12 14v4M17 14v4" />
    <path d="M12 4l3 5h-6z" fill="GLYPH_PAINT" stroke="none" />
    <circle cx="12" cy="16" r="1.8" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Slope Slider — a diagonal line with slope-triangle showing rise/run.
  "Slope Slider": g(<>
    <path d="M4 20L20 6" />
    <path d="M4 20h10v-7.5" strokeDasharray="2 2" />
  </>),

  // Trajectory — a parabolic arc with start and end points marked.
  "Trajectory": g(<>
    <path d="M3 19q5-16 18 0" />
    <circle cx="3" cy="19" r="1.8" fill="GLYPH_PAINT" stroke="none" />
    <circle cx="21" cy="19" r="1.8" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Funcion — an f(x) style curve in a graph frame, the canonical
  // "function" symbol pattern.
  "Funcion": g(<>
    <path d="M4 21V3M4 21h18" />
    <path d="M5 18q4-12 8-7t9-7" />
  </>),

  // Area Architect — two shapes (square + triangle) hinting composition.
  "Area Architect": g(<>
    <rect x="3" y="11" width="9" height="9" />
    <path d="M14 20l5-9 5 9z" transform="translate(-2,0)" />
  </>),

  // Transform Tetris — a tetromino shape with a curved rotation arrow.
  "Transform Tetris": g(<>
    <path d="M5 8h4v4h-4zM9 8h4v4h-4zM9 12h4v4h-4z" />
    <path d="M16 6a6 6 0 0 1 4 6" />
    <path d="M21 10l-1 2-2-1" strokeLinejoin="round" />
  </>),

  // Estimate — the "~" approximation tilde with a tight up-down companion
  // to read as "order of magnitude" rather than identical to Mathic Quest.
  "Estimate": g(<>
    <path d="M4 10q3-3 6 0t6 0t4-1" />
    <text x="12" y="20" textAnchor="middle" fontSize="9" fontWeight="600" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">10ⁿ</text>
  </>),

  // Triangle Forge — three nested triangles, hinting at the "named triangles"
  // family being constructed.
  "Triangle Forge": g(<>
    <path d="M12 3l9 16h-18z" />
    <path d="M12 9l5 9h-10z" />
  </>),

  // Modular — a circle with tick marks (the modular clock).
  "Modular": g(<>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4" />
    <path d="M12 12l4-3" strokeLinecap="round" />
  </>),

  // Constructor — a compass-and-straightedge motif: two arc strokes
  // forming the bisector pattern.
  "Constructor": g(<>
    <path d="M12 6L7 21M12 6l5 15" />
    <path d="M5 16a8 8 0 0 1 14 0" />
  </>),
  // ---- NerdTools EXPANSION (36 new tools) ----
  // Family 1 — Level & Measure
  // Level Pro: bubble level with a "+" precision crosshair indicating multi-mode.
  "Level Pro": g(<>
    <rect x="3" y="9" width="18" height="6" rx="2" />
    <circle cx="12" cy="12" r="2" />
    <path d="M12 5v4M12 15v4M5 12h2M17 12h2" />
  </>),
  // Picture-Hanging Guide: three framed pictures with a center pin.
  "Picture-Hanging Guide": g(<>
    <rect x="3" y="7" width="5" height="6" rx="0.5" />
    <rect x="10" y="7" width="5" height="6" rx="0.5" />
    <rect x="17" y="7" width="4" height="6" rx="0.5" />
    <path d="M3 18h18" />
    <circle cx="12.5" cy="6" r="1" fill="GLYPH_PAINT" stroke="none" />
  </>),
  // Object Height: a vertical object with an angle arc looking up.
  "Object Height": g(<>
    <path d="M19 21V5l-1 0v16" />
    <path d="M4 21h17" />
    <path d="M5 19 19 6" strokeDasharray="2 2" />
    <path d="M9 19a4 4 0 0 0 0-3" />
  </>),
  // Symmetry Checker: a butterfly-ish bilateral shape over a center axis.
  "Symmetry Checker": g(<>
    <path d="M12 3v18" strokeDasharray="2 2" />
    <path d="M12 7c-4 0-6 3-6 5s2 5 6 5" />
    <path d="M12 7c4 0 6 3 6 5s-2 5-6 5" />
  </>),
  // Photo Straightener: a tilted rectangle with rotation arrow.
  "Photo Straightener": g(<>
    <rect x="4" y="6" width="16" height="12" rx="1" transform="rotate(-8 12 12)" />
    <path d="M18 4a6 6 0 0 1 2 4" />
    <path d="M20 4l-2 .5L19 6" />
  </>),

  // Family 2 — Visual & Camera
  // Magnifier: classic loupe with a "+" inside (for the zoom).
  "Magnifier": g(<>
    <circle cx="10" cy="10" r="6" />
    <path d="M14.5 14.5L21 21" />
    <path d="M10 7v6M7 10h6" />
  </>),
  // Color Picker: an eyedropper.
  "Color Picker": g(<>
    <path d="M16 3l5 5-2 2-5-5z" />
    <path d="M14 5l-10 10v5h5l10-10" />
  </>),
  // Brightness Meter: a sun with rays + a dial mark.
  "Brightness Meter": g(<>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4" />
  </>),
  // Grid Overlay Camera: a frame with a 3x3 grid inside.
  "Grid Overlay Camera": g(<>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <path d="M9 5v14M15 5v14M3 10h18M3 14h18" />
  </>),
  // Before/After: two squares overlapping with a vertical divider.
  "Before / After": g(<>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <path d="M12 5v14" />
    <circle cx="12" cy="12" r="2" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Family 3 — Sound & Audio
  // Tone Generator: sine wave.
  "Tone Generator": g(<>
    <path d="M2 12c2.5 0 2.5-7 5-7s2.5 14 5 14 2.5-7 5-7 2.5 5 5 5" />
  </>),
  // Tuning Fork: two prongs.
  "Tuning Fork": g(<>
    <path d="M9 3v9a3 3 0 0 0 6 0V3" />
    <path d="M12 15v6" />
    <path d="M10 21h4" />
  </>),
  // Metronome: trapezoidal body with pendulum.
  "Metronome": g(<>
    <path d="M8 4h8l3 17H5z" />
    <path d="M12 21V8l3-2" />
    <circle cx="15" cy="6" r="0.9" fill="GLYPH_PAINT" stroke="none" />
  </>),
  // Spectrogram: scrolling waterfall (vertical bars of varying height).
  "Spectrogram": g(<>
    <path d="M3 18V6M6 18v-8M9 18v-5M12 18v-9M15 18v-4M18 18v-7M21 18v-3" />
  </>),

  // Family 4 — Outdoor & Navigation
  // GPS Coordinates: pin drop.
  "GPS Coordinates": g(<>
    <path d="M12 21s-7-6-7-12a7 7 0 0 1 14 0c0 6-7 12-7 12z" />
    <circle cx="12" cy="9" r="2.5" />
  </>),
  // Altimeter: mountain with a small flag.
  "Altimeter": g(<>
    <path d="M3 19l5-9 4 5 3-3 6 7z" />
    <path d="M14 7v-3l4 1.5L14 7" fill="GLYPH_PAINT" stroke="GLYPH_PAINT" />
  </>),
  // Sun & Moon: sun + crescent moon nesting.
  "Sun & Moon": g(<>
    <circle cx="9" cy="9" r="3" />
    <path d="M9 3v1M9 14v1M3 9h1M14 9h1M5 5l0.7 0.7M13 13l-0.7-0.7M5 13l0.7-0.7M13 5l-0.7 0.7" />
    <path d="M19 15a4 4 0 1 1-3-7 5 5 0 0 0 3 7z" />
  </>),
  // Trail Marker: arrow pointing back with a flag pin.
  "Trail Marker": g(<>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 4l-3 8h6z" />
    <circle cx="12" cy="12" r="1" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Family 5 — DIY & Workshop
  // Metal Detector: search-coil shape (loop on a stick).
  "Metal Detector": g(<>
    <ellipse cx="9" cy="16" rx="6" ry="3" />
    <path d="M14 14l5-9" />
    <path d="M17 3l3 2-2 3" />
  </>),
  // Square Checker: an L-square.
  "Square Checker": g(<>
    <path d="M4 4v16h16" />
    <path d="M4 8h2M4 12h2M4 16h2M8 20v-2M12 20v-2M16 20v-2" />
  </>),
  // Drill Bit Guide: a drill bit.
  "Drill Bit Guide": g(<>
    <path d="M12 3v10" />
    <path d="M9 13l3 5 3-5" />
    <path d="M12 18v3" />
    <path d="M10 6l4 2M10 9l4 2" />
  </>),
  // Roof Pitch: a roof triangle with rise/run notation.
  "Roof Pitch": g(<>
    <path d="M3 18L12 6l9 12z" />
    <path d="M12 18V9" strokeDasharray="2 2" />
  </>),
  // Coverage Calculator: paint roller / bucket.
  "Coverage Calculator": g(<>
    <rect x="5" y="6" width="11" height="5" rx="0.5" />
    <path d="M5 11v2h11v-2" />
    <path d="M16 8h4M12 13v2c0 1.5-1.5 2-1.5 3.5S12 21 12 21" />
  </>),

  // Family 6 — Math & Engineering
  // Fraction Calculator: a fraction bar with numbers.
  "Fraction Calculator": g(<>
    <text x="12" y="11" textAnchor="middle" fontSize="9" fontWeight="700" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">a</text>
    <path d="M5 13h14" />
    <text x="12" y="22" textAnchor="middle" fontSize="9" fontWeight="700" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">b</text>
  </>),
  // Right Triangle: a right triangle with the small right-angle mark.
  "Right Triangle": g(<>
    <path d="M4 19h16L4 6z" />
    <path d="M16 19v-3h3" />
  </>),
  // Resistor Color Code: a horizontal pill with three colored bands.
  "Resistor Color Code": g(<>
    <path d="M2 12h4M18 12h4" />
    <rect x="6" y="9" width="12" height="6" rx="2" />
    <path d="M9 9v6M12 9v6M15 9v6" />
  </>),
  // Bolt Torque: a hex bolt head with a wrench-ish twist.
  "Bolt Torque": g(<>
    <path d="M12 4l6 3v7l-6 3-6-3V7z" />
    <path d="M12 8l3 1.5v3l-3 1.5-3-1.5v-3z" />
    <path d="M20 18a5 5 0 0 1-4 3" />
  </>),

  // Family 7 — Time & Counting
  // Interval Timer: clock with interval ticks.
  "Interval Timer": g(<>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 13V8M12 13l3 2" />
    <path d="M9 2h6" />
    <path d="M12 5v1" strokeDasharray="0.3 1" />
  </>),
  // Tally Counter: 4 vertical tally marks + diagonal.
  "Tally Counter": g(<>
    <path d="M6 5v14M9 5v14M12 5v14M15 5v14" />
    <path d="M4 8l13 8" />
  </>),
  // Reaction Timer: a stopwatch with a lightning bolt.
  "Reaction Timer": g(<>
    <circle cx="12" cy="14" r="7" />
    <path d="M9 4h6" />
    <path d="M13 10l-3 5h2l-1 3 3-5h-2z" fill="GLYPH_PAINT" stroke="none" />
  </>),

  // Family 8 — Sensor Lab
  // Vibration Meter: scrolling waveform.
  "Vibration Meter": g(<>
    <path d="M2 12h3l1-5 2 10 2-7 2 7 2-10 2 5h6" />
  </>),
  // Magnetic Field: a U-magnet with field lines.
  "Magnetic Field": g(<>
    <path d="M5 5v8a7 7 0 0 0 14 0V5" />
    <path d="M5 5h3v6M19 5h-3v6" />
    <path d="M12 18v3" />
    <path d="M9 21h6" />
  </>),
  // Hand Steadiness: hand silhouette with steadiness mark.
  "Hand Steadiness": g(<>
    <path d="M9 7v8" />
    <path d="M12 5v10" />
    <path d="M15 7v8" />
    <path d="M6 11v4a5 5 0 0 0 5 5h2a5 5 0 0 0 5-5v-2" />
    <path d="M18 13c0-1 1-1.5 1-3" />
  </>),
  // Shake / Rotation: rotation arrow + impact marks.
  "Shake / Rotation": g(<>
    <path d="M5 12a7 7 0 0 1 14 0" />
    <path d="M19 12l-2 2-2-2" />
    <path d="M5 12l2-2 2 2" />
    <path d="M12 17v3M10 19h4" />
  </>),

  // Family 9 — Practical / Generators
  // Currency Converter: $ ⇄ € with two arrows.
  "Currency Converter": g(<>
    <text x="6" y="14" textAnchor="middle" fontSize="11" fontWeight="700" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">$</text>
    <text x="18" y="14" textAnchor="middle" fontSize="11" fontWeight="700" fill="GLYPH_PAINT" stroke="none" fontFamily="DM Sans, system-ui, sans-serif">€</text>
    <path d="M9 17h6M12 15l3 2-3 2" />
  </>),
  // Password Generator: a key with dots.
  "Password Generator": g(<>
    <circle cx="7" cy="12" r="3.5" />
    <path d="M10.5 12h10M17 9v3M20 9v3" />
  </>),
  // EXIF Remover: photo with an "X" mark.
  "EXIF Remover": g(<>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <circle cx="8" cy="10" r="1.5" />
    <path d="M21 19l-7-8-4 5-3-3-4 6" />
    <path d="M16 7l4 4M20 7l-4 4" />
  </>),
};

export function glyphFor(name) {
  const e = TOOL_GLYPHS[name];
  if (!e) return null;
  return <svg {...S} aria-hidden="true">{e.children}</svg>;
}

/**
 * Render a glyph at a given pixel size with a chosen stroke. `stroke`
 * may be a solid hex or an SVG paint reference like "url(#id)". When a
 * gradient is used, pass its <defs> via `defs` so it lives in the same
 * SVG. Used by ToolCard for the no-plate Option-1 gradient treatment.
 */
// Recursively swap the GLYPH_PAINT sentinel (used by fill-based glyphs
// like the "23" SigFigs mark) for the real paint, so fill glyphs color
// exactly like stroke glyphs instead of falling back to near-black.
function applyPaint(node, paint) {
  return Children.map(node, (child) => {
    if (!isValidElement(child)) return child;
    const p = { ...child.props };
    if (p.fill === "GLYPH_PAINT") p.fill = paint;
    if (p.stroke === "GLYPH_PAINT") p.stroke = paint;
    const kids = p.children ? applyPaint(p.children, paint) : p.children;
    return cloneElement(child, p, kids);
  });
}

export function renderGlyph(name, { size = 22, stroke = "currentColor", strokeWidth = 1.8, defs = null } = {}) {
  const e = TOOL_GLYPHS[name];
  if (!e) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {defs}
      {applyPaint(e.children, stroke)}
    </svg>
  );
}

// ─── V57.8: Family-folder glyphs ─────────────────────────────────────
// Used by FolderTile on the NerdTools home page. Separate map from
// TOOL_GLYPHS because these key off family IDs, not tool names.
// Same 24x24 viewBox, 1.8 stroke, currentColor — matches the per-tool
// glyph style so folders feel native to the suite.
export const FAMILY_GLYPHS = {
  // 1. Level & Measure — crosshair / target
  "level-measure": g(<>
    <circle cx="12" cy="12" r="8"/>
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
  </>),
  // 2. Visual & Camera — camera body
  "visual-camera": g(<>
    <rect x="3" y="7" width="18" height="13" rx="2"/>
    <path d="M9 7 L10 4 H14 L15 7"/>
    <circle cx="12" cy="13.5" r="3.5"/>
  </>),
  // 3. Sound & Audio — waveform bars
  "sound-audio": g(<>
    <path d="M4 12v0"/>
    <path d="M8 9v6"/>
    <path d="M12 5v14"/>
    <path d="M16 8v8"/>
    <path d="M20 11v2"/>
  </>),
  // 4. Outdoor & Navigation — mountain range
  "outdoor-nav": g(<>
    <path d="M3 19 L9 10 L13 14 L17 8 L21 19 Z"/>
  </>),
  // 5. DIY & Workshop — ruler + pencil crossed
  "diy-workshop": g(<>
    <path d="M4 8 L8 4 L20 16 L16 20 Z"/>
    <path d="M5 9 L7 7 M8 12 L10 10 M11 15 L13 13 M14 18 L16 16"/>
    <path d="M18 3 L21 6 L9 18 L6 21 L7 17 Z"/>
  </>),
  // 6. Math & Engineering — π (tall variant)
  "math-engineering": g(<>
    <path d="M2.5 4 L21.5 4"/>
    <path d="M7 4 L7 21"/>
    <path d="M5.5 21 L8.5 21"/>
    <path d="M16 4 L16 19 Q16 21 18 21 L20 21"/>
  </>),
  // 7. Time & Counting — stopwatch
  "time-counting": g(<>
    <circle cx="12" cy="13" r="8"/>
    <path d="M12 13 V8"/>
    <path d="M12 13 L15 15"/>
    <path d="M10 3 H14"/>
    <path d="M12 3 V5"/>
  </>),
  // 8. Sensor Lab — signal waveform
  "sensor-lab": g(<>
    <path d="M2 12 L6 12 L8 6 L11 18 L13 8 L15 16 L17 12 L22 12"/>
  </>),
  // 9. Practical & Generators — toolbox
  "practical": g(<>
    <rect x="3" y="8" width="18" height="12" rx="2"/>
    <path d="M9 8 V6 a2 2 0 0 1 2 -2 h2 a2 2 0 0 1 2 2 V8"/>
    <path d="M3 13 H21"/>
    <rect x="10.5" y="11.5" width="3" height="3" rx="0.5" fill="currentColor" stroke="none"/>
  </>),
  // 10. Novel & Useful — star
  "novel-useful": g(<>
    <path d="M12 3 L13.5 9 L20 9.5 L15 13.5 L17 20 L12 16 L7 20 L9 13.5 L4 9.5 L10.5 9 Z"/>
  </>),
};

// Render a family glyph as a sized SVG. Used by FolderTile.
export function renderFamilyGlyph(familyId, { size = 28, stroke = "currentColor", strokeWidth = 1.8 } = {}) {
  const e = FAMILY_GLYPHS[familyId];
  if (!e) return null;
  // Math glyph uses thicker stroke for π weight; sensible default elsewhere.
  const sw = familyId === "math-engineering" ? 2.2 : strokeWidth;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {applyPaint(e.children, stroke)}
    </svg>
  );
}
